import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Dialog } from 'primeng/dialog';
import { Subscription } from 'rxjs';
import { SERVICE_ENDPOINTS } from '../../../core/configs/service-endpoints';
import { AuthService } from '../../../core/services/auth.service';
import { HelperService } from '../../../core/services/helper.service';
import { I18nService } from '../../../core/services/i18n.service';
import { PaymentMethodDataCardComponent } from '../../../custom/components/sps-payment-method-data-card/payment-method-data-card.component';
import { LayoutService } from '../../../layout/services/layout.service';
import { SidebarMenuService } from '../../../layout/services/sidebar-menu.service';
import { PaymentDataUpdate } from '../../dtos/payment-type/payment-data-update';
import { EditPaymentDialogType } from '../../enums/payment/edit-payment-dialog-type';
import { PaymentMethodName } from '../../enums/payment/payment-method-name';
import { DialogTableDataRow } from '../../interfaces/dialog-table-data-row';
import { EditPaymentDialog } from '../../interfaces/payment/edit-payment-dialog';
import { PaymentData } from '../../interfaces/payment/payment-data';
import { PaymentInvalidFlags } from '../../interfaces/payment/payment-invalid-flags';
import { BankAccountToTableDataMapper } from '../../mapper/payment/bank-account-to-table-data-mapper.service';
import { EmailNotificationToTableDataMapper } from '../../mapper/payment/email-notification-to-table-data-mapper.service';
import { PaymentTypeToPaymentTypeUpdateDtoMapperService } from '../../mapper/payment/payment-type-to-payment-type-update-dto-mapper.service';
import { PaymentTypeToTableDataMapper } from '../../mapper/payment/payment-type-to-table-data-mapper.service';
import { ShopIntegrationToTableDataMapper } from '../../mapper/payment/shop-integration-to-table-data-mapper.service';
import { PaymentDataService } from '../../services/payment-data.service';
import { BaseComponent } from '../base/base.component';
import { MotoPaymentDialogComponent } from '../ifs-moto/moto-payment-dialog/moto-payment-dialog.component';
import { EditFormBankAccountComponent } from './edit-form-bank-account/edit-form-bank-account.component';
import { EditFormEmailNotificationComponent } from './edit-form-email-notification/edit-form-email-notification.component';
import { EditFormPaymentTypeComponent } from './edit-form-payment-type/edit-form-payment-type.component';

@Component({
  selector: 'app-payment-method-edit',
  imports: [
    FormsModule,
    PaymentMethodDataCardComponent,
    Dialog,
    EditFormPaymentTypeComponent,
    Button,
    EditFormBankAccountComponent,
    EditFormEmailNotificationComponent,
    Card,
    PrimeTemplate,
    MotoPaymentDialogComponent
  ],
  templateUrl: './payment-method-edit.component.html'
})
export class PaymentMethodEditComponent extends BaseComponent implements OnInit, AfterViewInit {

  constructor(public authService: AuthService,
              public i18n: I18nService,
              private layoutService: LayoutService,
              private sidebarMenuService: SidebarMenuService,
              public helperService: HelperService,
              private messageService: MessageService,
              public paymentDataService: PaymentDataService,
              private paymentTypeMapper: PaymentTypeToTableDataMapper,
              private paymentTypeUpdateMapper: PaymentTypeToPaymentTypeUpdateDtoMapperService,
              private bankAccountMapper: BankAccountToTableDataMapper,
              private shopIntegrationMapper: ShopIntegrationToTableDataMapper,
              private emailNotificationMapper: EmailNotificationToTableDataMapper) {
    super();
  }

  protected readonly PaymentMethodName = PaymentMethodName;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private paymentId: string;
  paymentData: PaymentData = null;
  paymentDataResetClone: PaymentData = null;
  paymentActive: boolean = false;
  editPaymentDialog: EditPaymentDialog = {visible: false};
  protected readonly PaymentDialogType = EditPaymentDialogType;
  paymentInvalidFlags: PaymentInvalidFlags;
  ibanIsValid: boolean = true;

  /**
   * Data for Payment-Data Cards
   */
  tableDataPaymentType: DialogTableDataRow[] = [];
  tableDataBankAccount: DialogTableDataRow[] = [];
  tableDataShopIntegration: DialogTableDataRow[] = [];
  tableDataEmailNotification: DialogTableDataRow[] = [];

  /**
   * Data for virtual terminal (MOTO)
   */
  showVirtualTerminal: boolean = false;


  ngOnInit(): void {
    this.loading = true;
    this.route.paramMap.subscribe(params => {
      this.paymentId = params.get('id');
      this.getPaymentData();
    });
  }

  ngAfterViewInit(): void {
    /**
     * Some controls need refresh after language-changes
     */
    this.subscriptions.add(
      this.i18n.languageChanged.subscribe({
        next: value => {
          this.refreshControls = true;
          setTimeout(() => {
            this.refreshControls = false;
          }, 10);
        }
      })
    );
  }

  /**
   * get payment type data from service
   * @private
   */
  private getPaymentData() {
    this.paymentDataService.findById(SERVICE_ENDPOINTS.GET_PAYMENT_TYPE, this.paymentId).subscribe({
      next: result => {
        if (result.successful) {
          this.paymentData = result.dto;
          this.paymentActive = (this.paymentData.status === 'LIVE');
          /**
           * Data-Mapping for Data-Table
           */
          this.mapPaymentTableData();
          this.loading = false;
          setTimeout(() => {
            /**
             * Initialize Breadcrumb-Items
             */
            this.i18n.breadcrumbMenuItems.set([
              {
                icon: 'pi pi-home',
                i18nLabelKey: 'MENUDASHBOARD',
                label: this.i18n.i18nLng()['MENUDASHBOARD'],
                routerLink: ['/dashboard'],
                routerLinkActive: false
              },
              {
                i18nLabelKey: 'MENUPAYMENTTYPESVIEW',
                label: this.i18n.i18nLng()['MENUPAYMENTTYPESVIEW'],
                routerLink: ['/paymentmethodsview'],
                routerLinkActive: false
              },
              {
                // icon: 'pi ',
                label: this.paymentData?.title,
                routerLinkActive: true
              }
            ]);
            /**
             * Sidebar-Menu Sync
             */
            this.layoutService.sidebarMenu.activateMenuItem(this.sidebarMenuService.findMenuItemById(this.sidebarMenuService.menuItems, 'm-pm-' + this.paymentId));
            /** try to autoscroll to the selected menu-item if there are many submenu items */
            setTimeout(() => {
              document.getElementById('m-pm-' + this.paymentId).scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest'
              });
            }, 500);
          }, 50);
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
          this.router.navigate(['']);
        }
      },
      error: err => {
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
        });
        this.router.navigate(['']);
      }
    })
  }

  /**
   * Data-Mapping for Data-Tables
   * @private
   */
  private mapPaymentTableData() {
    this.tableDataPaymentType = this.paymentTypeMapper.map(this.paymentData);
    this.tableDataBankAccount = this.bankAccountMapper.map(this.paymentData);
    this.tableDataShopIntegration = this.shopIntegrationMapper.map(this.paymentData, this.authService.organisationData?.displayCustomerNumber);
    this.tableDataEmailNotification = this.emailNotificationMapper.map(this.paymentData);
  }

  showEditDialog(paymentDialogType: EditPaymentDialogType) {
    this.resetPaymentInvalidFlags();
    this.paymentDataResetClone = this.helperService.jsonCopy(this.paymentData);
    this.editPaymentDialog = {
      type: paymentDialogType,
      visible: true
    };
    switch (paymentDialogType) {
      case EditPaymentDialogType.PAYMENTTYPE:
        this.editPaymentDialog.i18nTitleKey = 'EDITPAYMENTTYPE';
        break;
      case EditPaymentDialogType.BANKACCOUNT:
        this.editPaymentDialog.i18nTitleKey = 'EDITBANKACCOUNT';
        break;
      case EditPaymentDialogType.EMAILNOTIFICATION:
        this.editPaymentDialog.i18nTitleKey = 'EDITEMAILNOTIFICATION';
        break;
    }
  }

  /**
   * Persist Data-Changes
   */
  savePaymentData() {

    /**
     * Check valid payment data
     */
    let valid: boolean = true;
    this.resetPaymentInvalidFlags();

    /**
     * in TEST-Mode IBAN is not a required field
     */
    if (this.paymentData.status === 'TEST' && (!this.paymentData?.IBAN || this.paymentData?.IBAN.trim().length === 0)) {
      this.ibanIsValid = true;
    }
    if (!this.paymentData?.title || this.paymentData.title?.trim().length === 0) {
      valid = false;
      this.paymentInvalidFlags.title = true;
    }

    /**
     * Validation of Payment Details if status is LIVE
     * TODO extend for other payment types - if needed
     */
    if (this.paymentData.status === 'LIVE') {
      switch (this.paymentData.paymentMethod) {
        case PaymentMethodName.PAYPAL:
          if (!this.paymentData?.payPalEmailAddress || this.paymentData.payPalEmailAddress?.trim().length === 0 || !this.helperService.isValidEmail(this.paymentData.payPalEmailAddress)) {
            valid = false;
            this.paymentInvalidFlags.accountHolder = true;
          }
          break;
        default:
          if (!this.paymentData?.accountHolder || this.paymentData.accountHolder?.trim().length === 0) {
            valid = false;
            this.paymentInvalidFlags.accountHolder = true;
          }
          if (!this.paymentData?.IBAN || this.paymentData.IBAN?.trim().length === 0 || !this.ibanIsValid) {
            valid = false;
            this.paymentInvalidFlags.IBAN = true;
          }
          if (!this.paymentData?.BIC || this.paymentData.BIC?.trim().length === 0) {
            valid = false;
            this.paymentInvalidFlags.BIC = true;
          }
          if (!this.paymentData?.bank || this.paymentData.bank?.trim().length === 0) {
            valid = false;
            this.paymentInvalidFlags.bank = true;
          }
      }
    }

    /**
     * Validate for checked E-Mail Notification and valid E-Mail Addresses
     */
    if (this.paymentData.emailNotification && (!this.paymentData?.emailAddress || this.paymentData.emailAddress?.trim().length === 0)) {
      valid = false;
      this.paymentInvalidFlags.emailAddress = true;
    }
    if (this.paymentData?.emailAddress) {
      this.paymentData.emailAddress = this.paymentData.emailAddress.trim();
      if (this.paymentData?.emailAddress.length > 0) {
        if (!this.helperService.isValidEmailList(this.paymentData.emailAddress)) {
          valid = false;
          this.paymentInvalidFlags.invalidEmailFormat = true;
        }
      }
    }

    /**
     * persist if OK
     */
    if (valid && this.ibanIsValid) {
      const paymentDataDto: PaymentDataUpdate = this.paymentTypeUpdateMapper.map(this.paymentData);
      this.paymentDataService.update(SERVICE_ENDPOINTS.UPDATE_PAYMENT_TYPE + this.paymentData.id, paymentDataDto).subscribe({
        next: result => {
          if (result.successful) {
            this.messageService.add({
              life: 3500,
              severity: 'success',
              summary: this.i18n.i18nLng()['PAYMENTMETHOD'] + ' ' + this.i18n.i18nLng()[this.paymentData.paymentMethod],
              detail: this.i18n.i18nLng()['HTTPDATAUPDATESUCCESS']
            });
            this.editPaymentDialog.visible = false;
            this.mapPaymentTableData();
            const subscription: Subscription = this.paymentDataService.initTenantPaymentData().subscribe({
              next: success => {
                subscription.unsubscribe()
                this.sidebarMenuService.changeMenuStatus('m-pm-' + this.paymentData.id, this.paymentData.status);
                this.sidebarMenuService.changeMenuLabel('m-pm-' + this.paymentData.id, this.paymentData.title);
              }
            });
          } else {
            this.messageService.add({
              sticky: true,
              severity: 'error',
              summary: this.i18n.i18nLng()['HTTPDATAERROR'],
              detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
            });
          }
        },
        error: err => {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
      });
    } else {
      /**
       * Show missing LIVE-Data Message in payment type dialog if switched to live
       * only needed for active payment types -> this should not happen on valid workflows
       */
      if (!valid && this.paymentData.status === 'LIVE') {
        this.paymentInvalidFlags.missingLiveData = true;
      }
    }
  }

  /**
   * Cancel Edit-Dialog
   * Reset Data from Reset-Clone
   */
  cancelEditPaymentData() {
    this.paymentData.title = this.paymentDataResetClone.title;
    this.paymentData.status = this.paymentDataResetClone.status;
    this.paymentData.accountHolder = this.paymentDataResetClone.accountHolder;
    this.paymentData.IBAN = this.paymentDataResetClone.IBAN;
    this.paymentData.BIC = this.paymentDataResetClone.BIC;
    this.paymentData.bank = this.paymentDataResetClone.bank;
    this.paymentData.emailNotification = this.paymentDataResetClone.emailNotification;
    this.paymentData.emailAddress = this.paymentDataResetClone.emailAddress;
    this.editPaymentDialog.visible = false;
  }

  /**
   * Reset all invalid flags for payment data
   * @private
   */
  private resetPaymentInvalidFlags() {
    this.paymentInvalidFlags = {
      paymentMethod: false,
      title: false,
      accountHolder: false,
      IBAN: false,
      BIC: false,
      bank: false,
      emailAddress: false,
      invalidEmailFormat: false,
      missingLiveData: false,
    };
  }

  /**
   * Set the validity flags for the IBAN
   * @param valid
   */
  onIbanValidated(valid: boolean) {
    this.ibanIsValid = valid;
    this.paymentInvalidFlags.IBAN = !valid;
  }

  /**
   * Viirtual Termninal für Credit Card MOTO
   * @protected
   */
  protected showMotoDialog() {
    this.showVirtualTerminal = true;
  }

  /**
   * Viirtual Termninal für Credit Card MOTO
   * @protected
   */
  protected closeMotoDialog() {
    this.showVirtualTerminal = false;
  }

}
